import { useCallback, useRef, useState } from "react";
import { UseDocumentExportOptions } from "../models/DocumentExportOptions";
import { UseDocumentExportResult } from "../models/DocumentExportResult";
import { downloadDocument } from "../services/documentExportService";
import { isNativeShareAvailable, shareDocument } from "../services/nativeShareService";
import { printDocument } from "../utils/documentPrint";

const DEFAULT_SHARING_WARNING =
  "This document contains confidential information. Confirm that it is appropriate to share it using an external application.";

export const useDocumentExport = ({
  fileName,
  title,
  shareText,
  generateDocument,
  security,
}: UseDocumentExportOptions): UseDocumentExportResult => {
  const [isGenerating, setIsGenerating] = useState(false);
  const generationInProgress = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const run = useCallback(
    async (action: (blob: Blob) => Promise<void> | void, failureMessage: string) => {
      if (generationInProgress.current) return;
      generationInProgress.current = true;
      setIsGenerating(true);
      setError(null);
      setMessage(null);
      try {
        await action(await generateDocument());
      } catch (caught) {
        console.error("Document export failed", caught);
        setError(failureMessage);
      } finally {
        generationInProgress.current = false;
        setIsGenerating(false);
      }
    },
    [generateDocument],
  );

  const download = useCallback(
    () => run((blob) => downloadDocument(blob, fileName), "The PDF could not be downloaded. Please try again."),
    [fileName, run],
  );

  const print = useCallback(
    () => run((blob) => printDocument(blob), "The document could not be opened for printing. Check whether your browser blocked a new window."),
    [run],
  );

  const share = useCallback(async () => {
    if (security?.confirmBeforeSharing || security?.confidential) {
      const confirmed = window.confirm(security.sharingWarning ?? DEFAULT_SHARING_WARNING);
      if (!confirmed) return;
    }

    return run(async (blob) => {
      const result = await shareDocument(blob, { fileName, title, text: shareText });
      if (result.status === "unsupported") {
        downloadDocument(blob, fileName);
        setMessage("The PDF has been downloaded. Open your preferred sharing app and attach the downloaded file.");
      }
    }, "The PDF could not be shared. Please try again.");
  }, [fileName, run, security, shareText, title]);

  return {
    isGenerating,
    error,
    message,
    download,
    print,
    share,
    canNativeShare: isNativeShareAvailable(),
  };
};
